import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const FormSection = styled.section`
  padding: 80px 0;
  background-color: #121E42;
  color: white;
`;

const FormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 40px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
`;

const FormTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 0.5rem;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const FormSubtitle = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.8);
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  font-weight: 500;
  color: white;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  transition: border-color 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #00A8E8;
    box-shadow: 0 0 0 2px rgba(0, 168, 232, 0.2);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  transition: border-color 0.3s ease;
  
  option {
    background-color: #121E42;
    color: white;
  }
  
  &:focus {
    outline: none;
    border-color: #00A8E8;
    box-shadow: 0 0 0 2px rgba(0, 168, 232, 0.2);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 5px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.95rem;
`;

const RadioInput = styled.input`
  cursor: pointer;
  width: 18px;
  height: 18px;
`;

const SubmitButton = styled(motion.button)`
  padding: 14px 20px;
  background-color: #00E5FF;
  color: #121E42;
  border: none;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #00C9E0;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
    transform: none;
  }
`;

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    course: '',
    city: '',
    phone: '',
    isGraduated: 'SIM'
  });
  
  const courses = [
    'MBA em Enfermagem em Cardiologia e Hemodinâmica',
    'MBA em Enfermagem em Emergência',
    'MBA em UTI Adulto com Ênfase em Cardiologia',
    'MBA em Enfermagem Dermatológica com Ênfase em Feridas',
    'MBA em Centro Cirúrgico, Sala de Recuperação Anestésica e CME',
    'MBA em Enfermagem em Estomaterapia',
    'MBA em UTI Neonatal e Pediátrica',
    'MBA em Enfermagem em Oncologia',
    'MBA em Enfermagem do Trabalho',
    'MBA em Gestão em Saúde',
    'MBA em Cuidados Paliativos',
    'MBA em Auditoria em Saúde'
  ];
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Formatar mensagem para WhatsApp exatamente como solicitado
    const message = `Novo Interesse - Pós Graduação
Nome: ${formData.name}
Telefone: ${formData.phone}
E-mail: ${formData.email}
Curso: ${formData.course}
Formado: ${formData.isGraduated}`;
    
    // Codificar a mensagem para URL
    const encodedMessage = encodeURIComponent(message);
    
    // Número do WhatsApp
    const whatsappNumber = '5511972736170';
    
    // Criar URL do WhatsApp
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Abrir em nova aba
    window.open(whatsappUrl, '_blank');
  };
  
  const isFormValid = formData.name && formData.email && formData.course && formData.phone;
  
  return (
    <FormSection id="contato">
      <div className="container">
        <FormContainer>
          <FormTitle>Quer saber qual pós combina com você?</FormTitle>
          <FormSubtitle>Preencha os dados e fale com um especialista</FormSubtitle>
          
          <StyledForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">Nome completo</Label>
              <Input 
                type="text" 
                id="name" 
                name="name" 
                value={formData.name}
                onChange={handleChange}
                placeholder="Digite seu nome completo"
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="email">E-mail</Label>
              <Input 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email}
                onChange={handleChange}
                placeholder="Digite seu e-mail"
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="phone">Telefone (com DDD)*</Label>
              <Input 
                type="tel" 
                id="phone" 
                name="phone" 
                value={formData.phone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="city">Cidade</Label>
              <Input 
                type="text" 
                id="city" 
                name="city" 
                value={formData.city}
                onChange={handleChange}
                placeholder="Digite sua cidade"
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="course">Curso de interesse</Label>
              <Select 
                id="course" 
                name="course" 
                value={formData.course}
                onChange={handleChange}
              >
                <option value="">Selecione um curso</option>
                {courses.map((course, index) => (
                  <option key={index} value={course}>{course}</option>
                ))}
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>Formado</Label>
              <RadioGroup>
                <RadioLabel>
                  <RadioInput 
                    type="radio" 
                    name="isGraduated" 
                    value="SIM" 
                    checked={formData.isGraduated === 'SIM'}
                    onChange={handleChange}
                  />
                  Sim
                </RadioLabel>
                <RadioLabel>
                  <RadioInput 
                    type="radio" 
                    name="isGraduated" 
                    value="NÃO" 
                    checked={formData.isGraduated === 'NÃO'}
                    onChange={handleChange}
                  />
                  Não
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            
            <SubmitButton 
              type="submit" 
              disabled={!isFormValid}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              Falar com um consultor
            </SubmitButton>
          </StyledForm>
        </FormContainer>
      </div>
    </FormSection>
  );
};

export default ContactForm;
